import React from "react";
import { Box, Avatar, Typography, Paper } from "@mui/material";
import dayjs from "dayjs";
import { Message } from "shared/entities";

interface MessageBubbleProps {
  message: Message;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ message }) => {
  const formatTime = (timestamp: string): string => {
    return dayjs(timestamp).format("h:mm A");
  };

  const getInitials = (name: string): string => {
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: message.isOwn ? "row-reverse" : "row",
        gap: 1,
        mb: 2,
        alignItems: "flex-end",
      }}
    >
      {/* Avatar */}
      {!message.isOwn && (
        <Avatar
          src={message.senderAvatar}
          sx={{
            width: 32,
            height: 32,
            backgroundColor: "#178B8E",
            fontSize: "0.875rem",
            fontWeight: 600,
          }}
        >
          {getInitials(message.senderName)}
        </Avatar>
      )}

      {/* Message bubble */}
      <Box
        sx={{
          maxWidth: "70%",
          display: "flex",
          flexDirection: "column",
          alignItems: message.isOwn ? "flex-end" : "flex-start",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            px: 2,
            py: 1.5,
            borderRadius: message.isOwn
              ? "16px 16px 4px 16px"
              : "16px 16px 16px 4px",
            backgroundColor: message.isOwn ? "#178B8E" : "#FFFFFF",
            color: message.isOwn ? "#FFFFFF" : "text.primary",
            border: message.isOwn ? "none" : "1px solid #E0E0E0",
            wordBreak: "break-word",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              whiteSpace: "pre-wrap",
              lineHeight: 1.5,
              color: message.isOwn ? "#FFFFFF" : "text.primary",
            }}
          >
            {message.content}
          </Typography>
        </Paper>

        {/* Timestamp */}
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            mt: 0.5,
            fontSize: "0.7rem",
            px: 1,
          }}
        >
          {formatTime(message.timestamp)}
        </Typography>
      </Box>

      {/* Placeholder for own avatar (to maintain spacing) */}
      {message.isOwn && <Box sx={{ width: 32, height: 32 }} />}
    </Box>
  );
};

export default MessageBubble;
